import React from "react";
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from "@mui/material";
import {
  Archive,
  ArrowCounterClockwise,
  PauseCircle,
  PencilLine,
  Prohibit,
  Trash,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GoalActionMenuProps {
  /** The element that anchors the menu — typically the overflow IconButton */
  anchorEl: HTMLElement | null;
  /** Whether the menu is open */
  open: boolean;
  /** Called when the menu should close (backdrop click, Escape key, item click) */
  onClose: () => void;
  /** Called when "Edit Goal" is selected */
  onEdit: () => void;
  /** Called when "Put On Hold" is selected */
  onPutOnHold: () => void;
  /** Called when "Discontinue" is selected */
  onDiscontinue: () => void;
  /** Called when "Archive Goal" is selected (only when the goal is active) */
  onArchive: () => void;
  /** Called when "Unarchive Goal" is selected (only when the goal is archived) */
  onUnarchive?: () => void;
  /** Called when "Delete Goal" is selected */
  onDelete: () => void;
  /**
   * Whether the goal is archived. Drives two pieces of behavior:
   * - the archive action toggles between "Archive Goal" and "Unarchive Goal"
   * - the destructive "Delete Goal" action is only enabled once archived
   * Defaults to `false`.
   */
  isArchived?: boolean;
}

// ---------------------------------------------------------------------------
// GoalActionMenu
// ---------------------------------------------------------------------------

/**
 * Overflow action menu for a single goal in the Goals grid.
 * Renders Edit, Put On Hold, Discontinue, and an archive action that toggles
 * between "Archive Goal" (active goals) and "Unarchive Goal" (archived goals).
 * The destructive "Delete Goal" action (separated by a Divider and styled in
 * error.main) is only enabled once the goal is archived.
 *
 * Typical usage: anchor to the GoalCard's overflow IconButton via `onMenuOpen`.
 *
 * @param props
 * @param props.anchorEl     - DOM element that anchors the popover position
 * @param props.open         - Whether the menu is visible
 * @param props.onClose      - Called on backdrop click or Escape key
 * @param props.onEdit       - Handler for "Edit Goal"
 * @param props.onPutOnHold  - Handler for "Put On Hold"
 * @param props.onDiscontinue - Handler for "Discontinue"
 * @param props.onArchive    - Handler for "Archive Goal" (active goals)
 * @param props.onUnarchive  - Handler for "Unarchive Goal" (archived goals)
 * @param props.onDelete     - Handler for "Delete Goal"
 * @param props.isArchived   - Whether the goal is archived (drives the toggle + delete gating)
 */
const GoalActionMenu: React.FC<GoalActionMenuProps> = ({
  anchorEl,
  open,
  onClose,
  onEdit,
  onPutOnHold,
  onDiscontinue,
  onArchive,
  onUnarchive = () => {},
  onDelete,
  isArchived = false,
}) => {
  const handleItem = (callback: () => void) => () => {
    callback();
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: { elevation: 3 },
      }}
    >
      <MenuItem onClick={handleItem(onEdit)} aria-label="Edit goal">
        <ListItemIcon>
          <PencilLine size={20} />
        </ListItemIcon>
        <ListItemText>
          <Typography component="p" variant="body2">
            Edit Goal
          </Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={handleItem(onPutOnHold)} aria-label="Put goal on hold">
        <ListItemIcon>
          <PauseCircle size={20} />
        </ListItemIcon>
        <ListItemText>
          <Typography component="p" variant="body2">
            Put On Hold
          </Typography>
        </ListItemText>
      </MenuItem>
      <MenuItem onClick={handleItem(onDiscontinue)} aria-label="Discontinue goal">
        <ListItemIcon>
          <Prohibit size={20} />
        </ListItemIcon>
        <ListItemText>
          <Typography component="p" variant="body2">
            Discontinue
          </Typography>
        </ListItemText>
      </MenuItem>
      {isArchived ? (
        <MenuItem onClick={handleItem(onUnarchive)} aria-label="Unarchive goal">
          <ListItemIcon>
            <ArrowCounterClockwise size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography component="p" variant="body2">
              Unarchive Goal
            </Typography>
          </ListItemText>
        </MenuItem>
      ) : (
        <MenuItem onClick={handleItem(onArchive)} aria-label="Archive goal">
          <ListItemIcon>
            <Archive size={20} />
          </ListItemIcon>
          <ListItemText>
            <Typography component="p" variant="body2">
              Archive Goal
            </Typography>
          </ListItemText>
        </MenuItem>
      )}
      <Divider key="delete-divider" />
      <MenuItem
        key="delete-item"
        onClick={handleItem(onDelete)}
        aria-label="Delete goal"
        sx={{ color: "error.main" }}
        disabled={!isArchived}
      >
        <ListItemIcon sx={{ color: "inherit" }}>
          <Trash size={20} />
        </ListItemIcon>
        <ListItemText>
          <Typography component="p" variant="body2">
            Delete Goal
          </Typography>
        </ListItemText>
      </MenuItem>
    </Menu>
  );
};

export default GoalActionMenu;
